import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { feedbackApi, guestsApi, checksApi } from '../services/api';
import {
  ThumbsUp,
  ThumbsDown,
  Heart,
  X,
  Check,
  User,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import clsx from 'clsx';

type Sentiment = 'loved' | 'liked' | 'disliked' | 'wont_order';

interface FeedbackItem {
  orderItemId: string;
  menuItemId: string | null;
  menuItemName: string;
  sentiment: Sentiment | null;
}

interface GuestProfileForm {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

export default function FeedbackPage() {
  const { checkId } = useParams();
  const navigate = useNavigate();

  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);
  const [showGuestForm, setShowGuestForm] = useState(false);
  // Digital receipt (Apple Tap to Pay req 5.10). These hooks MUST live above
  // the loading early-return — hooks after a conditional return change the
  // hook count between renders, which crashes React (white screen after
  // payment).
  const [receiptEmail, setReceiptEmail] = useState('');
  const [receiptState, setReceiptState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [guestForm, setGuestForm] = useState<GuestProfileForm>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
  });

  // Fetch items for feedback
  const { data: checkData, isLoading } = useQuery({
    queryKey: ['feedback-items', checkId],
    queryFn: async () => {
      const response = await feedbackApi.getCheckItems(checkId!);
      return response.data;
    },
    enabled: !!checkId,
  });

  // Initialize feedback items when data loads
  useEffect(() => {
    if (checkData?.items) {
      setFeedbackItems(
        checkData.items.map((item: any) => ({
          orderItemId: item.orderItemId,
          menuItemId: item.menuItemId,
          menuItemName: item.name,
          sentiment: null,
        }))
      );
    }
  }, [checkData]);

  // Submit feedback mutation
  const submitMutation = useMutation({
    mutationFn: async () => {
      const itemFeedback = feedbackItems
        .filter(f => f.sentiment !== null)
        .map(f => ({
          orderItemId: f.orderItemId,
          menuItemId: f.menuItemId,
          menuItemName: f.menuItemName,
          sentiment: f.sentiment!,
        }));

      if (itemFeedback.length === 0) {
        return { success: true };
      }

      const payload: any = { itemFeedback };

      // Add guest profile if provided
      if (showGuestForm && guestForm.firstName && guestForm.lastName) {
        payload.createGuestProfile = {
          firstName: guestForm.firstName,
          lastName: guestForm.lastName,
          phone: guestForm.phone || undefined,
          email: guestForm.email || undefined,
        };
      }

      return feedbackApi.submit(checkId!, payload);
    },
    onSuccess: () => {
      navigate('/tables');
    },
  });

  const handleSetSentiment = (orderItemId: string, sentiment: Sentiment) => {
    setFeedbackItems(items =>
      items.map(item =>
        item.orderItemId === orderItemId
          ? { ...item, sentiment: item.sentiment === sentiment ? null : sentiment }
          : item
      )
    );
  };

  const sendReceipt = async () => {
    if (!checkId || !receiptEmail.includes('@')) return;
    setReceiptState('sending');
    const res = await checksApi.emailReceipt(checkId, receiptEmail.trim());
    setReceiptState(res.success ? 'sent' : 'failed');
  };

  const handleSkip = () => {
    navigate('/tables');
  };

  const handleDone = () => {
    submitMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const hasFeedback = feedbackItems.some(f => f.sentiment !== null);

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button
          onClick={handleSkip}
          className="p-1.5 -ml-1 rounded-lg hover:bg-slate-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h2 className="font-semibold text-lg">Quick Feedback</h2>
          <p className="text-sm text-slate-500">How did they enjoy it?</p>
        </div>
      </div>

      {/* Email receipt */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        {receiptState === 'sent' ? (
          <p className="text-sm text-emerald-600 font-medium">Receipt sent — check your inbox.</p>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="email"
              inputMode="email"
              autoCapitalize="none"
              value={receiptEmail}
              onChange={(e) => { setReceiptEmail(e.target.value); if (receiptState === 'failed') setReceiptState('idle'); }}
              placeholder="Email a receipt (optional)"
              className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              onClick={sendReceipt}
              disabled={!receiptEmail.includes('@') || receiptState === 'sending'}
              className="px-4 py-2 rounded-xl bg-primary-600 text-white text-sm font-medium disabled:opacity-40"
            >
              {receiptState === 'sending' ? 'Sending…' : 'Send'}
            </button>
          </div>
        )}
        {receiptState === 'failed' && (
          <p className="text-xs text-red-500 mt-1.5">Couldn't send just now — the printed receipt still works.</p>
        )}
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 scroll-bounce">
        {feedbackItems.map((item) => (
          <div
            key={item.orderItemId}
            className="bg-white rounded-xl shadow-sm p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 pr-4">
                <span className="font-medium text-slate-800">
                  {item.menuItemName}
                </span>
              </div>

              <div className="flex items-center gap-2">
                {/* Loved - Heart */}
                <button
                  onClick={() => handleSetSentiment(item.orderItemId, 'loved')}
                  className={clsx(
                    'p-3 rounded-xl transition-all',
                    item.sentiment === 'loved'
                      ? 'bg-rose-100 text-rose-600 ring-2 ring-rose-300'
                      : 'bg-slate-100 text-slate-400 hover:bg-rose-50 hover:text-rose-400'
                  )}
                >
                  <Heart
                    className={clsx(
                      'w-6 h-6',
                      item.sentiment === 'loved' && 'fill-current'
                    )}
                  />
                </button>

                {/* Liked - Thumbs Up */}
                <button
                  onClick={() => handleSetSentiment(item.orderItemId, 'liked')}
                  className={clsx(
                    'p-3 rounded-xl transition-all',
                    item.sentiment === 'liked'
                      ? 'bg-emerald-100 text-emerald-600 ring-2 ring-emerald-300'
                      : 'bg-slate-100 text-slate-400 hover:bg-emerald-50 hover:text-emerald-400'
                  )}
                >
                  <ThumbsUp className="w-6 h-6" />
                </button>

                {/* Disliked - Thumbs Down */}
                <button
                  onClick={() => handleSetSentiment(item.orderItemId, 'disliked')}
                  className={clsx(
                    'p-3 rounded-xl transition-all',
                    item.sentiment === 'disliked'
                      ? 'bg-amber-100 text-amber-600 ring-2 ring-amber-300'
                      : 'bg-slate-100 text-slate-400 hover:bg-amber-50 hover:text-amber-400'
                  )}
                >
                  <ThumbsDown className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Guest Profile Option */}
        {!checkData?.guestInfo && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <button
              onClick={() => setShowGuestForm(!showGuestForm)}
              className="w-full p-4 flex items-center gap-3 text-left"
            >
              <div className={clsx(
                'p-2 rounded-lg',
                showGuestForm ? 'bg-primary-100 text-primary-600' : 'bg-slate-100 text-slate-500'
              )}>
                <User className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <span className="font-medium text-slate-700">
                  {showGuestForm ? 'Guest Profile' : 'Add Guest Profile'}
                </span>
                <p className="text-sm text-slate-500">
                  Save preferences for their next visit
                </p>
              </div>
            </button>

            {showGuestForm && (
              <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="First Name *"
                    value={guestForm.firstName}
                    onChange={(e) => setGuestForm(f => ({ ...f, firstName: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-slate-50 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Last Name *"
                    value={guestForm.lastName}
                    onChange={(e) => setGuestForm(f => ({ ...f, lastName: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-slate-50 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={guestForm.phone}
                  onChange={(e) => setGuestForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-slate-50 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <input
                  type="email"
                  placeholder="Email (optional)"
                  value={guestForm.email}
                  onChange={(e) => setGuestForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-slate-50 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}
          </div>
        )}

        {/* Existing Guest Info */}
        {checkData?.guestInfo && (
          <div className="bg-primary-50 rounded-xl p-4 border border-primary-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <User className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <span className="font-medium text-primary-800">
                  {checkData.guestInfo.firstName} {checkData.guestInfo.lastName}
                </span>
                <p className="text-sm text-primary-600">
                  Feedback will be saved to their profile
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Spacer */}
        <div className="h-4" />
      </div>

      {/* Bottom Actions */}
      <div className="bg-white border-t border-slate-200 p-4">
        <div className="flex gap-3">
          <button
            onClick={handleSkip}
            className="flex-1 py-3.5 px-4 bg-slate-100 text-slate-600 rounded-xl font-medium flex items-center justify-center gap-2"
          >
            <X className="w-5 h-5" />
            Skip
          </button>
          <button
            onClick={handleDone}
            disabled={submitMutation.isPending}
            className={clsx(
              'flex-1 py-3.5 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors',
              hasFeedback
                ? 'bg-emerald-600 text-white active:bg-emerald-700'
                : 'bg-slate-800 text-white active:bg-slate-900'
            )}
          >
            {submitMutation.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5" />
                Done
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
